import { useState } from "react";

import { STOPLIGHT } from "@/lib/stoplight";
import { cn } from "@/lib/utils";

/**
 * Review-priority — a 0–3 "how fast do I want feedback" flag shown as escalating
 * exclamation marks that take ONE brand colour per level:
 *   1 = ! green (Notable) · 2 = !! amber (Important) · 3 = !!! red (Critical).
 *
 * `ReviewPriority` is the interactive control (modal): unset marks sit muted like
 * the close / ⋯ icons; click a mark to set that level, click the current top
 * level to clear. `ReviewPriorityBadge` is the read-only card indicator — just
 * the set marks, nothing when unflagged.
 */
const LABEL: Record<number, string> = {
  1: "Notable",
  2: "Important",
  3: "Critical",
};
const COLOR: Record<number, string> = {
  1: STOPLIGHT.green,
  2: STOPLIGHT.amber,
  3: STOPLIGHT.red,
};

export function ReviewPriority({
  value,
  onChange,
}: {
  value: number;
  onChange: (level: number) => void;
}) {
  const [hovered, setHovered] = useState(0);
  const shown = hovered || value;
  const color = COLOR[shown]; // one colour for every mark at the shown level

  return (
    <div
      role="group"
      aria-label="Review priority"
      className="mr-1.5 flex items-center"
      onMouseLeave={() => setHovered(0)}
    >
      {[1, 2, 3].map((n) => {
        const active = n <= shown;
        return (
          <button
            key={n}
            type="button"
            title={LABEL[n]}
            aria-label={LABEL[n]}
            aria-pressed={value >= n}
            onMouseEnter={() => setHovered(n)}
            onClick={() => onChange(value === n ? 0 : n)}
            style={active ? { color } : undefined}
            className={cn(
              "flex h-7 w-3.5 cursor-pointer items-center justify-center text-sm font-extrabold leading-none transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              active ? "opacity-100" : "text-muted-foreground opacity-70",
            )}
          >
            !
          </button>
        );
      })}
    </div>
  );
}

/** Read-only card indicator: the set marks in the level's colour; null if unflagged. */
export function ReviewPriorityBadge({ level }: { level: number }) {
  if (level < 1) return null;
  const color = COLOR[level];
  return (
    <span
      className="flex shrink-0 items-center"
      aria-label={LABEL[level]}
      title={LABEL[level]}
    >
      {Array.from({ length: level }, (_, i) => (
        <span
          key={i}
          style={{ color }}
          className="text-sm font-extrabold leading-none"
        >
          !
        </span>
      ))}
    </span>
  );
}
